import type { AxiosResponse } from 'axios';
import api from 'src/services/api';
import type {
  AdminCommentIndexRequest,
  AdminCommentUpdateRequest,
} from 'src/models/apis/comment/adminCommentRequest';
import {
  type AdminCommentIndexResponse,
  type AdminCommentResponse,
  toAdminComment,
} from 'src/models/apis/comment/adminCommentResponse';
import type { Comment } from 'src/models/comment';
import type {
  CommentIndexParams,
  CommentCreateParams,
  CommentUpdateParams,
} from 'src/models/apis/comment/commentRequest';
import {
  type CommentIndexResponse,
  type CommentResponse,
  toComment,
} from 'src/models/apis/comment/commentResponse';
import * as utils from './apiUtils';

export default {
  async index(params: CommentIndexParams): Promise<Comment[]> {
    const response = await api.get<CommentIndexResponse>(utils.getApiUrl('/comments'), {
      params,
    });
    return response.data.map(toComment);
  },
  async get(id: number): Promise<Comment> {
    const response = await api.get<CommentResponse>(utils.getApiUrl(`/comments/${id}`));
    return toComment(response.data);
  },
  async create(params: CommentCreateParams): Promise<Comment> {
    const formData = utils.transformFormData({ ...params });
    const response = await api.post<CommentResponse>(utils.getApiUrl('/comments'), formData);
    return toComment(response.data);
  },
  async update(id: number, params: CommentUpdateParams): Promise<Comment> {
    const formData = utils.transformFormData({ ...params });

    // putではFormDataのパラメータを受け取れないため
    // https://qiita.com/ky0he1_0t4/items/6cd8264f646bdf86cdb9
    const response = await api.post<CommentResponse>(utils.getApiUrl(`/comments/${id}`), formData, {
      headers: {
        'X-HTTP-Method-Override': 'PUT',
      },
    });
    return toComment(response.data);
  },
  async destroy(id: number): Promise<Comment> {
    const response = await api.delete<CommentResponse>(utils.getApiUrl(`/comments/${id}`));
    return toComment(response.data);
  },
  getCommentFileContent(commentId: number, commentFileId: number): Promise<AxiosResponse<Blob>> {
    return api.get(utils.getApiUrl(`/comments/${commentId}/comment_file/${commentFileId}/file`), {
      responseType: 'blob',
    });
  },

  async adminIndex(params: AdminCommentIndexRequest): Promise<Comment[]> {
    const response = await api.get<AdminCommentIndexResponse>(utils.getApiUrl('/admin/comments'), {
      params,
    });
    return response.data.map(toAdminComment);
  },
  async adminUpdate(id: number, params: AdminCommentUpdateRequest): Promise<Comment> {
    const formData = utils.transformFormData({ ...params });
    const response = await api.post<AdminCommentResponse>(utils.getApiUrl(`/admin/comments/${id}`), formData, {
      headers: {
        'X-HTTP-Method-Override': 'PUT',
      },
    });
    return toAdminComment(response.data);
  },
  async adminDestroy(id: number): Promise<Comment> {
    const response = await api.delete<AdminCommentResponse>(utils.getApiUrl(`/admin/comments/${id}`));
    return toAdminComment(response.data);
  },
};
